import React, { useMemo } from "react";
import UAUCObservationTable from "./UAUCObservationTable";
import {
  getAssigneeObjById,
  getDueAt,
  getOpenStartDate,
  isClosedStatus,
} from "../../utils/uaucAllUtils";

function numberFrom(value, fallback = 0) {
  if (typeof value === "string") {
    const match = value.match(/-?\d+(\.\d+)?/);
    const parsed = match ? Number(match[0]) : NaN;
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function percent(value, total) {
  const n = numberFrom(value);
  const d = numberFrom(total);

  if (d <= 0) return 0;

  return Math.min(100, Math.max(0, (n / d) * 100));
}

export default function UAUCAssigneeSection({
  section,
  ratings,
  observerBoxShow,
  onOpenEdit,
}) {
  const rating = ratings?.[section.id];

  const assigneeObj = useMemo(() => {
    if (section.id === "__unassigned__") return null;

    const sample =
      section.items.find((item) => getAssigneeObjById(item, section.id)) ||
      section.items[0];

    return getAssigneeObjById(sample, section.id);
  }, [section]);

  const performance = useMemo(() => {
    const allItems = Array.isArray(section.items) ? section.items : [];
    const openItems = allItems.filter((item) => !isClosedStatus(item?.status));
    const closedItems = allItems.filter((item) => isClosedStatus(item?.status));
    const now = Date.now();

    const openCount = numberFrom(rating?.openCount, openItems.length);
    const resolvedCount = numberFrom(rating?.resolvedCount, closedItems.length);
    const closedCount = numberFrom(rating?.closedCount, resolvedCount);
    const apiOverdue = numberFrom(rating?.totalOverdue);
    const avgClosedDays = numberFrom(rating?.avgClosingTimeDays);
    const avgOpenDays = numberFrom(rating?.avgOpenDays);

    const openOverdue = openItems.reduce((count, item) => {
      const dueAt = getDueAt(item);
      return count + (dueAt && now > dueAt.getTime() ? 1 : 0);
    }, 0);

    const closedLate = closedItems.reduce((count, item) => {
      const dueAt = getDueAt(item);
      const closedAt =
        item?.closedOn || item?.closedAt || item?.resolvedAt || item?.updatedAt;

      return (
        count +
        (dueAt && closedAt && new Date(closedAt).getTime() > dueAt.getTime()
          ? 1
          : 0)
      );
    }, 0);

    const openAgeDaysTotal = openItems.reduce((sum, item) => {
      const opened = getOpenStartDate(item);
      if (!opened) return sum;

      const ageMs = now - new Date(opened).getTime();
      const ageDays = Number.isFinite(ageMs)
        ? Math.max(0, ageMs / (24 * 3600e3))
        : 0;

      return sum + ageDays;
    }, 0);

    const totalAssigned = Math.max(openCount + resolvedCount, allItems.length);

    const totalSlaRisk = Math.max(apiOverdue, openOverdue + closedLate);
    const onTimeOpen = Math.max(0, openItems.length - openOverdue);
    const onTimeClosed = Math.max(0, closedCount - closedLate);

    const combinedDenom = Math.max(0, closedCount + openItems.length);
    const combinedAvgDays =
      combinedDenom > 0
        ? (avgClosedDays * closedCount + openAgeDaysTotal) / combinedDenom
        : 0;

    const openPct = percent(openCount, totalAssigned);
    const resolvedPct = percent(resolvedCount, totalAssigned);
    const slaPct = percent(onTimeOpen + onTimeClosed, totalAssigned);
    const slaRiskPct = percent(totalSlaRisk, totalAssigned);
    const speedScore = Math.max(0, 100 - combinedAvgDays * 5);
    const efficiencyPct = (slaPct / 100) * speedScore;

    return {
      openCount,
      resolvedCount,
      totalSlaRisk,
      avgClosedDays,
      avgOpenDays,
      combinedAvgDays,
      openPct,
      resolvedPct,
      slaPct,
      slaRiskPct,
      efficiencyPct,
    };
  }, [rating, section.items]);

  return (
    <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-200 bg-slate-50 px-4 py-4">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h5 className="text-base font-black tracking-tight text-slate-950">
                {section.name}
              </h5>

              <span className="rounded-full bg-sky-50 px-2.5 py-1 text-xs font-black text-sky-700 ring-1 ring-sky-200">
                {section.items.length} Observations
              </span>
            </div>

            {assigneeObj && (
              <ul className="mt-3 grid grid-cols-1 gap-2 text-xs sm:grid-cols-2">
                {observerBoxShow.map((fieldKey) => {
                  const value = assigneeObj[fieldKey];
                  if (!value) return null;

                  return (
                    <li
                      key={fieldKey}
                      className="rounded-xl border border-slate-200 bg-white px-3 py-2 shadow-sm"
                    >
                      <span className="font-black uppercase tracking-wide text-slate-400">
                        {fieldKey}
                      </span>
                      <span className="ml-2 font-bold text-slate-800">
                        {value}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="w-full rounded-2xl border border-slate-200 bg-white p-3 shadow-sm xl:max-w-2xl">
            {!rating ? (
              <p className="text-xs font-bold text-slate-400">
                Loading performance...
              </p>
            ) : (
              <>
                <div className="flex flex-wrap gap-2">
                  <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-black text-emerald-700 ring-1 ring-emerald-200">
                    Closed {performance.resolvedCount} (
                    {performance.resolvedPct.toFixed(1)}%)
                  </span>

                  <span className="rounded-full bg-amber-50 px-2.5 py-1 text-xs font-black text-amber-700 ring-1 ring-amber-200">
                    Open {performance.openCount} (
                    {performance.openPct.toFixed(1)}%)
                  </span>

                  <span className="rounded-full bg-red-50 px-2.5 py-1 text-xs font-black text-red-700 ring-1 ring-red-200">
                    SLA Risk {performance.totalSlaRisk} (
                    {performance.slaRiskPct.toFixed(1)}%)
                  </span>
                </div>

                <div className="mt-3 grid grid-cols-1 gap-2 text-xs font-bold text-slate-600 sm:grid-cols-4">
                  <div>
                    Avg Closed
                    <div className="mt-1 text-sm font-black text-slate-950">
                      {performance.avgClosedDays
                        ? performance.avgClosedDays.toFixed(2)
                        : "-"}
                    </div>
                  </div>

                  <div>
                    Avg Open
                    <div className="mt-1 text-sm font-black text-slate-950">
                      {performance.avgOpenDays
                        ? performance.avgOpenDays.toFixed(2)
                        : performance.combinedAvgDays
                          ? performance.combinedAvgDays.toFixed(2)
                          : "-"}
                    </div>
                  </div>

                  <div>
                    SLA
                    <div
                      className={`mt-1 text-sm font-black ${
                        performance.slaPct >= 80
                          ? "text-emerald-700"
                          : performance.slaPct >= 50
                            ? "text-amber-700"
                            : "text-red-700"
                      }`}
                    >
                      {performance.slaPct.toFixed(1)}%
                    </div>
                  </div>

                  <div>
                    Efficiency
                    <div className="mt-1 text-sm font-black text-sky-700">
                      {performance.efficiencyPct.toFixed(1)}%
                    </div>
                  </div>
                </div>

                <div className="relative mt-3 h-2 overflow-hidden rounded-full bg-slate-200">
                  <div
                    className="h-full rounded-full bg-emerald-500"
                    style={{ width: `${performance.slaPct}%` }}
                  />
                  <div
                    className="absolute left-0 top-0 h-full rounded-full bg-red-500/40"
                    style={{ width: `${performance.slaRiskPct}%` }}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <UAUCObservationTable items={section.items} onOpenEdit={onOpenEdit} />
    </section>
  );
}
